import { getContext, hasContext, setContext } from "svelte";

const FIELD_NAME = Symbol("FIELD_NAME")
const FORM_NAME = Symbol("FORM_NAME")
const FORM_STATE = Symbol("FORM_STATE")

type NameContext = Array<string|number>;
export type JSONValue =
  | string
  | number
  | boolean
  | null
  | { [key: string]: JSONValue }
  | JSONValue[];
export type JSONObject = { [key: string]: JSONValue };
export type FormContext = { data: JSONObject, errors: JSONObject };

export function getFieldNameContext(){
  return (getContext(FIELD_NAME) || []) as NameContext;
}

export function setFieldNameContext(v: NameContext){
  setContext(FIELD_NAME, v);
}

export function getFormNameContext(){
  return (getContext(FORM_NAME) || []) as NameContext;
}

export function setFormNameContext(v: NameContext){
  setContext(FORM_NAME, v);
}

export function appendFieldNameContext(...names: (string | number | undefined)[]) {
  let context = getFormNameContext();
  context = [...context, ...(names.filter(v=>v) as NameContext)];
  setFieldNameContext(context);
}

export function appendFormNameContext(...names: NameContext) {
  let context = getFieldNameContext();
  context = [...context, ...names];
  setFormNameContext(context);
}

export function fieldName(name: string) {
  let context = getFieldNameContext();
  if(!context.length) return name;
  let [first, ...rest] = [...context, name];
  return `${first}[${rest.join("][")}]`
}

export function registerFormState(formStateFn:()=>FormContext) {
  if(!hasContext(FORM_STATE)) setContext(FORM_STATE, formStateFn);
  return new DataField();
}

function dig(data : JSONObject, keys : Array<string|number>, throwError : boolean = false) {
  let pointer : JSONValue = data
  for(const key of keys) {
    pointer = pointer[key];
    if(pointer === null || typeof pointer === "undefined") {
      if(!throwError) break;
      const message = `Could not find data under [${keys.join(", ")}]`
      console.error(message, $state.snapshot(data))
      throw new Error(message);
    }
  }
  return pointer;
}

type ErrorReportingElement = HTMLButtonElement | HTMLFieldSetElement | HTMLInputElement | HTMLOutputElement | HTMLSelectElement | HTMLTextAreaElement

class DataField {
  #formContext : ()=>FormContext
  #nameContext : NameContext
  #fullNameContext : NameContext
  #localName : string | number | undefined
  #name? : string
  #value = $derived.by(()=>dig(this.#formContext().data, this.#fullNameContext))
  #error = $derived.by(()=>dig(this.#formContext().errors, this.#fullNameContext))
  #errorElement? : ErrorReportingElement
  #errorReporting? : ()=>(()=>void)

  constructor(localName? : string) {
    this.#fullNameContext = [...getFieldNameContext()]
    if(localName) this.#fullNameContext.push(localName);
    this.#nameContext = [...this.#fullNameContext];
    this.#localName = this.#nameContext.pop();
    this.#formContext = getContext(FORM_STATE) as ()=>FormContext;
  }

  get name(){
    if(!this.#name) {
      let [first, ...rest] = this.#fullNameContext
      this.#name = `${first}${rest.length ? `[${rest.join("][")}]` : ""}`;
    }
    return this.#name;
  }

  get value() {
    return this.#value;
  }

  set value(v){
    const obj = dig(this.#formContext().data, this.#nameContext, true)
    if(!obj) {
      const msg = `Error setting ${this.#localName}: Object not found in dataContext at path [${this.#nameContext.join(",")}]`
      console.error(msg, {dataContext: this.#formContext().data, nameContext: this.#nameContext})
      throw msg
    }
    if(!this.#localName) {
      throw "DataField without a localName is read-only"
    }
    obj[this.#localName] = v
  }

  // So far only meaningful in DataForm components that are children of a NestedFormSet
  get nth() {
    const parentContext = this.#fullNameContext.slice(0, -1)
    const parent = dig(this.#formContext().data, parentContext)
    if(!Array.isArray(parent)) return undefined;
    const v = this.value
    const present = notDeleted(parent)
    for(let i=0; i < present.length; i++) {
      if(present[i] === v) return i;
    }
    return undefined; // This should only happen if this subform is currently deleted
  }

  getBoolean() {
    return !!this.#value && this.#value !== "false" && this.#value !== "f"
  }

  setBoolean(v : boolean) {
    this.value = v ? "true" : "false"
  }

  get asBoolean() {
    return [()=>this.getBoolean(), (v)=>this.setBoolean(v)] as const;
  }

  get error() {
    return this.#error;
  }

  set errorElement(el : ErrorReportingElement) {
    this.#errorElement = el
    if(!this.#errorReporting) {
      const unset = (event:Event)=>{
        (event.target as ErrorReportingElement)?.setCustomValidity("")
        event.target?.removeEventListener("input", unset)
      }
      this.#errorReporting = ()=>{
        const last = this.#errorElement
        if(this.#error) {
          this.#errorElement?.setCustomValidity(this.#error as string)
          this.#errorElement?.reportValidity()
          this.#errorElement?.addEventListener("input", unset)
        } else {
          this.#errorElement?.setCustomValidity("")
        }
        return ()=>{
          last?.setCustomValidity("")
        }
      }
      $effect(this.#errorReporting)
    }
  }
}

export type { DataField }

export function dataField(name? : string) {
  return new DataField(name);
}

export function localFormData() {
  return dig((getContext(FORM_STATE) as ()=>FormContext)().data, getFieldNameContext(), true) as JSONObject;
}

export function notDeleted(items) {
  return items.filter(item => !item._destroy)
}
